import { useEffect, useState } from 'react';
import { ladeListe, bekannteListe, anlegen, aendern, entfernen } from '../services/mitarbeiter';

/**
 * 👥 Einstellungen → Mitarbeiter: anlegen (Name + 4-stellige PIN), umbenennen, PIN ändern, entfernen.
 * Sobald mindestens einer angelegt ist, fragt jedes Gerät des Kontos zu Schichtbeginn nach Name + PIN
 * und ordnet Erfassen/Ausgeben zu. Entfernte Mitarbeiter bleiben in der Statistik sichtbar.
 */
export default function MitarbeiterVerwalten({ t, colors, onClose }) {
  const [liste, setListe] = useState(() => bekannteListe());
  const [laedt, setLaedt] = useState(true);
  const [name, setName] = useState('');
  const [pin, setPin] = useState('');
  const [fehler, setFehler] = useState('');
  const [bearbeite, setBearbeite] = useState(null); // { id, name, pin }
  const [busy, setBusy] = useState(false);
  const dark = colors?.darkMode;
  const farbe = { text: dark ? '#fff' : '#23272f', sub: dark ? '#aaa' : '#666', rand: dark ? '#444' : '#ddd', feld: dark ? '#1b1f27' : '#fff', zeile: dark ? '#2d333b' : '#f5f6f8' };

  useEffect(() => {
    ladeListe().then(setListe).catch(() => setFehler(t('staffs.offline'))).finally(() => setLaedt(false));
  }, [t]);

  const meldung = (e) => {
    if (e && e.status === 409) return t('staffs.nameTaken');
    if (e && e.status === 400 && e.data && e.data.error === 'pin') return t('staffs.pinFormat');
    if (e && e.status === 400 && e.data && e.data.error === 'zuviele') return t('staffs.tooMany');
    return t('staffs.offline');
  };
  const tu = async (fn) => {
    setBusy(true); setFehler('');
    try { await fn(); setListe(bekannteListe()); return true; } catch (e) { setFehler(meldung(e)); return false; } finally { setBusy(false); }
  };

  const neu = async () => {
    if (!name.trim()) { setFehler(t('staffs.nameMissing')); return; }
    if (!/^\d{4}$/.test(pin)) { setFehler(t('staffs.pinFormat')); return; }
    if (await tu(() => anlegen(name.trim(), pin))) { setName(''); setPin(''); }
  };
  const speichern = async () => {
    const patch = {};
    const alt = liste.find((m) => m.id === bearbeite.id);
    if (bearbeite.name.trim() && alt && bearbeite.name.trim() !== alt.name) patch.name = bearbeite.name.trim();
    if (bearbeite.pin) {
      if (!/^\d{4}$/.test(bearbeite.pin)) { setFehler(t('staffs.pinFormat')); return; }
      patch.pin = bearbeite.pin;
    }
    if (!Object.keys(patch).length) { setBearbeite(null); return; }
    if (await tu(() => aendern(bearbeite.id, patch))) setBearbeite(null);
  };
  const weg = async (m) => {
    if (!window.confirm(t('staffs.removeConfirm', { name: m.name }))) return;
    await tu(() => entfernen(m.id));
  };

  const feld = { padding: '10px', borderRadius: 8, border: `1px solid ${farbe.rand}`, fontSize: 15, background: farbe.feld, color: farbe.text, boxSizing: 'border-box', minWidth: 0 };
  const klein = { padding: '8px 10px', borderRadius: 8, border: `1px solid ${farbe.rand}`, background: 'transparent', color: farbe.text, fontSize: 13, cursor: 'pointer' };
  const pinFeld = (wert, setze, testid) => (
    <input data-testid={testid} type="password" inputMode="numeric" autoComplete="new-password" maxLength={4} value={wert} placeholder={t('staffs.pinPlaceholder')}
      onChange={(e) => setze(e.target.value.replace(/\D/g, '').slice(0, 4))} style={{ ...feld, width: 110, letterSpacing: wert ? '0.3em' : 'normal' }} />
  );

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 7600, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div data-testid="staffs-manage" style={{ background: dark ? '#23272f' : '#fff', color: farbe.text, borderRadius: 14, width: '100%', maxWidth: 480,
        maxHeight: '90vh', overflowY: 'auto', padding: 18, boxShadow: '0 10px 40px rgba(0,0,0,0.4)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
          <strong style={{ fontSize: 17 }}>👥 {t('staffs.title')}</strong>
          <button type="button" data-testid="staffs-close" onClick={onClose} aria-label={t('common.close')}
            style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: farbe.text }}>×</button>
        </div>
        <div style={{ fontSize: 13, color: farbe.sub, marginTop: 6, lineHeight: 1.5 }}>{t('staffs.explain')}</div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 14 }}>
          {laedt && !liste.length && <div style={{ fontSize: 13, color: farbe.sub }}>⏳</div>}
          {liste.map((m) => (
            <div key={m.id} data-testid="staffs-row" style={{ background: farbe.zeile, borderRadius: 8, padding: '8px 10px' }}>
              {bearbeite && bearbeite.id === m.id ? (
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                  <input data-testid="staffs-edit-name" value={bearbeite.name} maxLength={40} onChange={(e) => setBearbeite({ ...bearbeite, name: e.target.value })} style={{ ...feld, flex: 1 }} />
                  {pinFeld(bearbeite.pin, (p) => setBearbeite({ ...bearbeite, pin: p }), 'staffs-edit-pin')}
                  <button type="button" data-testid="staffs-edit-save" disabled={busy} onClick={speichern} style={{ ...klein, background: '#1976d2', color: '#fff', border: 'none' }}>{t('common.save')}</button>
                  <button type="button" onClick={() => setBearbeite(null)} style={klein}>{t('common.cancel')}</button>
                </div>
              ) : (
                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <span data-testid="staffs-row-name" style={{ flex: 1, fontWeight: 600, fontSize: 15, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>{m.name}</span>
                  <button type="button" data-testid="staffs-edit" onClick={() => { setBearbeite({ id: m.id, name: m.name, pin: '' }); setFehler(''); }} style={klein}>✏️ {t('staffs.edit')}</button>
                  <button type="button" data-testid="staffs-remove" disabled={busy} onClick={() => weg(m)} style={{ ...klein, color: '#e5484d' }}>{t('staffs.remove')}</button>
                </div>
              )}
            </div>
          ))}
        </div>

        <div style={{ marginTop: 14, paddingTop: 12, borderTop: `1px solid ${farbe.rand}` }}>
          <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 6 }}>➕ {t('staffs.add')}</div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            <input data-testid="staffs-new-name" value={name} maxLength={40} placeholder={t('staffs.namePlaceholder')} onChange={(e) => setName(e.target.value)} style={{ ...feld, flex: 1 }} />
            {pinFeld(pin, setPin, 'staffs-new-pin')}
            <button type="button" data-testid="staffs-new-save" disabled={busy} onClick={neu}
              style={{ ...klein, background: '#1976d2', color: '#fff', border: 'none', fontSize: 15, padding: '10px 14px' }}>{t('staffs.addButton')}</button>
          </div>
        </div>
        {fehler && <div data-testid="staffs-error" style={{ color: '#e5484d', fontSize: 13, marginTop: 8 }}>⚠️ {fehler}</div>}
        <div style={{ fontSize: 12, color: farbe.sub, marginTop: 12, lineHeight: 1.45 }}>{t('staffs.hint')}</div>
      </div>
    </div>
  );
}
